/**
 * Dialog de configuração do node "Criar Atividade no Negócio" (crm_create_activity)
 *
 * RELEASE: 1.1.0 - 2026-09-07 - Variável de saída migrada para o modal de destino.
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Permite escolher como localizar o deal (gatilho ou variável), o tipo de
 * atividade, título, descrição, responsável e variável de saída. Persistência
 * acontece via RPC `create_deal_activity` (executada no Flow Engine).
 *
 * Esta v1 NÃO trata agendamento — toda atividade entra como histórico.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InputWithVariables } from '../InputWithVariables';
import { TextareaWithVariables } from '../TextareaWithVariables';
import {
  ClipboardList,
  Link2,
  Type,
  AlignLeft,
  User,
  ArrowRight,
  HelpCircle,
  Zap,
  StickyNote,
  Phone,
  Mail,
  Calendar,
  Bell,
  MessageCircle,
  MapPin,
} from 'lucide-react';
import { FlowNode, CrmCreateActivityConfig } from '@/types/flows';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';

type ActivityType = CrmCreateActivityConfig['activity_type'];
type AssigneeMode = NonNullable<CrmCreateActivityConfig['assignee_mode']>;

interface ActivityTypeOption {
  value: ActivityType;
  label: string;
  Icon: typeof StickyNote;
}

/** Lista dos 7 tipos permitidos pela RPC create_deal_activity */
const ACTIVITY_TYPES: ActivityTypeOption[] = [
  { value: 'note',     label: 'Nota',     Icon: StickyNote },
  { value: 'call',     label: 'Ligação',  Icon: Phone },
  { value: 'email',    label: 'E-mail',   Icon: Mail },
  { value: 'meeting',  label: 'Reunião',  Icon: Calendar },
  { value: 'task',     label: 'Lembrete', Icon: Bell },
  { value: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
  { value: 'visit',    label: 'Visita',   Icon: MapPin },
];

interface ConfigureCrmCreateActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: CrmCreateActivityConfig) => void;
}

export function ConfigureCrmCreateActivityDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureCrmCreateActivityDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  const existing = node.data.config as CrmCreateActivityConfig | undefined;

  // ============== Estado: deal ==============
  const [dealSource, setDealSource] = useState<CrmCreateActivityConfig['deal_source']>(
    existing?.deal_source || 'from_trigger'
  );
  const [dealVariable, setDealVariable] = useState(existing?.deal_variable || '');
  const [dealVariableId, setDealVariableId] = useState(existing?.deal_variable_id || '');

  // ============== Estado: tipo e conteúdo ==============
  const [activityType, setActivityType] = useState<ActivityType>(existing?.activity_type || 'note');
  const [title, setTitle] = useState(existing?.title || '');
  const [description, setDescription] = useState(existing?.description || '');

  // ============== Estado: responsável ==============
  const [assigneeMode, setAssigneeMode] = useState<AssigneeMode>(existing?.assignee_mode || 'none');
  const [assigneeId, setAssigneeId] = useState(existing?.assignee_id || '');
  const [assigneeName, setAssigneeName] = useState(existing?.assignee_name || '');
  const [assigneeVariable, setAssigneeVariable] = useState(existing?.assignee_variable || '');
  const [assigneeVariableId, setAssigneeVariableId] = useState(existing?.assignee_variable_id || '');

  // ============== Estado: saída ==============
  const [outputVariable, setOutputVariable] = useState(existing?.output_variable || '');
  const [outputVariableId, setOutputVariableId] = useState(existing?.output_variable_id || '');

  const [showHelp, setShowHelp] = useState(false);

  // Sincroniza estado quando o dialog reabre
  useEffect(() => {
    if (!open) return;
    const cfg = node.data.config as CrmCreateActivityConfig | undefined;
    setDealSource(cfg?.deal_source || 'from_trigger');
    setDealVariable(cfg?.deal_variable || '');
    setDealVariableId(cfg?.deal_variable_id || '');
    setActivityType(cfg?.activity_type || 'note');
    setTitle(cfg?.title || '');
    setDescription(cfg?.description || '');
    setAssigneeMode(cfg?.assignee_mode || 'none');
    setAssigneeId(cfg?.assignee_id || '');
    setAssigneeName(cfg?.assignee_name || '');
    setAssigneeVariable(cfg?.assignee_variable || '');
    setAssigneeVariableId(cfg?.assignee_variable_id || '');
    setOutputVariable(cfg?.output_variable || '');
    setOutputVariableId(cfg?.output_variable_id || '');
  }, [open, node]);

  // Validação leve
  const isFormValid = (): boolean => {
    if (!activityType) return false;
    if (!title.trim()) return false;
    if (title.length > 200) return false;
    if (description && description.length > 2000) return false;
    if (dealSource === 'from_variable' && !dealVariable.trim()) return false;
    if (assigneeMode === 'manual' && !assigneeId) return false;
    if (assigneeMode === 'from_variable' && !assigneeVariable.trim()) return false;
    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }
    const config: CrmCreateActivityConfig = {
      nickname: nicknameRef.current?.getNickname() || undefined,

      deal_source: dealSource,
      deal_variable: dealSource === 'from_variable' ? dealVariable.trim() : undefined,
      deal_variable_id: dealSource === 'from_variable' ? dealVariableId || undefined : undefined,

      activity_type: activityType,
      title: title.trim(),
      description: description.trim() || undefined,

      assignee_mode: assigneeMode,
      assignee_id: assigneeMode === 'manual' ? assigneeId : undefined,
      assignee_name: assigneeMode === 'manual' ? assigneeName || undefined : undefined,
      assignee_variable: assigneeMode === 'from_variable' ? assigneeVariable.trim() : undefined,
      assignee_variable_id: assigneeMode === 'from_variable' ? assigneeVariableId || undefined : undefined,

      output_variable: outputVariable.trim() || undefined,
      output_variable_id: outputVariableId || undefined,
    };

    onSave(node.id, config);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-violet-600" />
                Configurar Criar Atividade
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Registra uma atividade (nota, ligação, e-mail, reunião, lembrete, WhatsApp ou visita) na timeline de um negócio do CRM
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              {/* Identificação */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* ===================== NEGÓCIO DE DESTINO ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <Link2 className="h-4 w-4" />
                  Negócio de destino
                </div>

                <RadioGroup
                  value={dealSource}
                  onValueChange={(v) => setDealSource(v as CrmCreateActivityConfig['deal_source'])}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="from_trigger" id="deal-trigger" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="deal-trigger" className="font-medium cursor-pointer flex items-center gap-1.5">
                        <Zap className="h-3.5 w-3.5 text-violet-500" />
                        Negócio do gatilho
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa o deal que disparou o fluxo (gatilho CRM com entity_type='deal')
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="from_variable" id="deal-var" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="deal-var" className="font-medium cursor-pointer">
                        ID do deal em variável
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Útil para usar o ID gerado por um node "Criar Negócio" anterior
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {dealSource === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={dealVariableId || dealVariable}
                    onChange={(v) => {
                      if (v) {
                        setDealVariableId(v.id);
                        setDealVariable(v.name);
                      } else {
                        setDealVariableId('');
                        setDealVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com ID do deal"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== TIPO DA ATIVIDADE ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <ClipboardList className="h-4 w-4" />
                  Tipo da atividade
                </div>

                <RadioGroup
                  value={activityType}
                  onValueChange={(v) => setActivityType(v as ActivityType)}
                  className="grid grid-cols-2 gap-2"
                >
                  {ACTIVITY_TYPES.map((opt) => {
                    const Icon = opt.Icon;
                    return (
                      <div
                        key={opt.value}
                        className="flex items-center space-x-2 p-2.5 rounded-lg border border-border hover:border-violet-400 transition-colors"
                      >
                        <RadioGroupItem value={opt.value} id={`act-${opt.value}`} />
                        <Label
                          htmlFor={`act-${opt.value}`}
                          className="font-medium cursor-pointer flex items-center gap-1.5 flex-1"
                        >
                          <Icon className="h-3.5 w-3.5 text-violet-500" />
                          {opt.label}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              </div>

              <Separator />

              {/* ===================== TÍTULO ===================== */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <Type className="h-4 w-4" />
                  Título *
                </div>
                <InputWithVariables
                  tenantId={tenantId}
                  value={title}
                  onChange={setTitle}
                  placeholder="Ex.: Follow-up sobre proposta"
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground">
                  Aceita variáveis no formato {'{{nome}}'} — máx. 200 caracteres
                </p>
              </div>

              {/* ===================== DESCRIÇÃO ===================== */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <AlignLeft className="h-4 w-4" />
                  Descrição (opcional)
                </div>
                <TextareaWithVariables
                  tenantId={tenantId}
                  value={description}
                  onChange={setDescription}
                  placeholder="Detalhes adicionais sobre a atividade..."
                  maxLength={2000}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Aceita variáveis — máx. 2000 caracteres
                </p>
              </div>

              <Separator />

              {/* ===================== RESPONSÁVEL ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <User className="h-4 w-4" />
                  Responsável (opcional)
                </div>

                <RadioGroup
                  value={assigneeMode}
                  onValueChange={(v) => setAssigneeMode(v as AssigneeMode)}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="none" id="ass-none" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="ass-none" className="font-medium cursor-pointer">
                        Sem responsável
                      </Label>
                      <p className="text-xs text-muted-foreground">A atividade fica sem autor associado</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="manual" id="ass-manual" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="ass-manual" className="font-medium cursor-pointer">
                        Usuário fixo
                      </Label>
                      <p className="text-xs text-muted-foreground">Selecione um usuário do sistema</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="deal_seller" id="ass-seller" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="ass-seller" className="font-medium cursor-pointer">
                        Vendedor do negócio
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Resolve em runtime usando o vendedor atribuído ao deal
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
                    <RadioGroupItem value="from_variable" id="ass-var" className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="ass-var" className="font-medium cursor-pointer">
                        De uma variável
                      </Label>
                      <p className="text-xs text-muted-foreground">Usa o user_id contido em variável do fluxo</p>
                    </div>
                  </div>
                </RadioGroup>

                {assigneeMode === 'manual' && (
                  <SystemUserSelector
                    value={assigneeId || null}
                    onSelect={(userId, user) => {
                      setAssigneeId(userId || '');
                      setAssigneeName(user?.full_name || user?.email || '');
                    }}
                    placeholder="Selecionar usuário..."
                  />
                )}

                {assigneeMode === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={assigneeVariableId || assigneeVariable}
                    onChange={(v) => {
                      if (v) {
                        setAssigneeVariableId(v.id);
                        setAssigneeVariable(v.name);
                      } else {
                        setAssigneeVariableId('');
                        setAssigneeVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com user_id"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== SAÍDA ===================== */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                  <ArrowRight className="h-4 w-4" />
                  Saída (opcional)
                </div>
                <Label>Armazenar ID da atividade em variável</Label>
                <VariableDestinationPicker
                  tenantId={tenantId}
                  value={outputVariableId || outputVariable}
                  valueName={outputVariable}
                  onChange={(v) => {
                    if (v) {
                      setOutputVariableId(v.id);
                      setOutputVariable(v.name);
                    } else {
                      setOutputVariableId('');
                      setOutputVariable('');
                    }
                  }}
                  allowClear
                  placeholder="Selecione ou crie uma variável"
                />
                <p className="text-xs text-muted-foreground">
                  Permite referenciar o ID da atividade em nodes posteriores
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isFormValid()}
              className="bg-violet-600 hover:bg-violet-700"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="crm_create_activity" />
    </>
  );
}
